/**
 * Proposal List Item Component
 *
 * Clickable list item for proposal entries with status indicators.
 * Click item or arrow button to trigger navigation/open details.
 *
 * @package oska
 */

/**
 * ProposalItem class
 * Manages a clickable proposal list item
 */
class ProposalItem {
  /**
   * @param {HTMLElement} element - The item element with [data-proposal-item]
   */
  constructor(element) {
    this.item = element;
    this.arrow = element.querySelector("[data-arrow]");
    this.id = element.dataset.id || "";

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    this.bindEvents();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Item click (excluding arrow button)
    this.item.addEventListener("click", (e) => {
      e.preventDefault();
      // If clicked on arrow or inside arrow, let arrow handle it
      if (
        this.arrow &&
        (e.target === this.arrow || this.arrow.contains(e.target))
      ) {
        return;
      }
      this.handleSelect();
    });

    // Arrow button click
    if (this.arrow) {
      this.arrow.addEventListener("click", (e) => {
        e.stopPropagation();
        this.handleSelect();
      });
    }

    // Keyboard support
    this.item.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.handleSelect();
      }
    });
  }

  /**
   * Handle selection/click action
   */
  handleSelect() {
    this.emitEvent();
  }

  /**
   * Emit custom event for navigation/React integration
   */
  emitEvent() {
    const text =
      this.item.querySelector(".js-proposal-text")?.textContent || "";
    const status = this.getStatus();
    const year =
      this.item.querySelector(".js-proposal-year")?.textContent?.trim() || "";

    const event = new CustomEvent("proposal-item:select", {
      bubbles: true,
      detail: {
        id: this.id,
        status,
        year,
        text: text.trim().substring(0, 100), // Truncate for display
        element: this.item,
      },
    });

    this.item.dispatchEvent(event);
  }

  /**
   * Get the status of the item
   * @returns {string} Status value (done, progress, pending, cancelled)
   */
  getStatus() {
    // Check data attribute first (new format)
    if (this.item.dataset.status) {
      return this.item.dataset.status;
    }
    // Fallback to class-based detection (legacy)
    if (this.item.classList.contains("proposal-item--done")) return "done";
    if (this.item.classList.contains("proposal-item--progress"))
      return "progress";
    if (this.item.classList.contains("proposal-item--pending"))
      return "pending";
    if (this.item.classList.contains("proposal-item--cancelled"))
      return "cancelled";
    return "unknown";
  }
}

/**
 * Initialize all proposal items on the page
 * @returns {ProposalItem[]} Array of initialized ProposalItem instances
 */
export default function initProposalItem() {
  const items = document.querySelectorAll("[data-proposal-item]");
  const instances = [];

  items.forEach((item) => {
    instances.push(new ProposalItem(item));
  });

  // Demo: Listen for events on styleguide page
  const output = document.getElementById("proposal-item-output");
  if (output) {
    document.addEventListener("proposal-item:select", (e) => {
      const { id, status, year, text } = e.detail;
      output.textContent = `Item #${id} clicked | Status: ${status} | Year: ${year} | "${text}..."`;
    });
  }

  return instances;
}
